import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { clearAuth } from './api/auth'
import { closeEventStream } from './api/events'
import { useSession } from './api/session'
import { AppShell } from './components/layout/AppShell'
import type { NavSection } from './components/layout/Sidebar'

/**
 * Eine Ansicht, wie sie in Navigation und Kopfzeile erscheint.
 *
 * Titel und Navigationseintrag stehen zusammen an EINER Stelle. Getrennt —
 * Navigation hier, Titel in der Ansicht selbst — liefen sie beim ersten
 * Umbenennen auseinander, und in der Kopfzeile stünde etwas anderes als im Menü.
 */
interface View {
  path: string
  label: string
  /** Zeichen für den schmalen Streifen auf dem Tablet. */
  icon: string
  title: string
  subtitle?: string
}

export const VIEWS: View[] = [
  {
    path: '/',
    label: 'Übersicht',
    // Ü, weil U schon die Komponenten tragen — und weil auf dem Tablet nur
    // das Zeichen sichtbar ist.
    icon: 'Ü',
    title: 'Übersicht',
    subtitle: 'was sich seit gestern Abend gerührt hat — nach Vorzeichen',
  },
  {
    path: '/exchanges',
    label: 'Handelsplätze',
    icon: 'B',
    title: 'Handelsplätze',
    subtitle: 'Börsen, Währungen und Handelszeiten',
  },
  {
    path: '/industries',
    label: 'Branchen',
    icon: 'Z',
    title: 'Branchen',
    subtitle: 'Wonach die Instrumente eingeordnet sind',
  },
  {
    path: '/commodities',
    label: 'Rohstoffe',
    icon: 'R',
    title: 'Rohstoffe',
    subtitle: 'Öl, Metalle und Agrargüter',
  },
  {
    path: '/vocabulary',
    label: 'Vokabular',
    icon: 'V',
    title: 'Vokabular',
    subtitle: 'Länder, Regionen, Bündnisse, Märkte und Krypto',
  },
  {
    path: '/instruments',
    label: 'Instrumente',
    icon: 'T',
    title: 'Instrumente',
    subtitle: 'Wertpapiere im Bestand',
  },
  {
    path: '/watchlist',
    label: 'Watchlist',
    icon: 'W',
    title: 'Watchlist',
    subtitle: 'Beobachtete Titel',
  },
  {
    path: '/depots',
    label: 'Depots',
    icon: 'O',
    title: 'Depots',
    subtitle: 'Je Konto eine Kasse — Orders, Positionen, Kontoauszug',
  },
  {
    path: '/assessments',
    label: 'Einschätzungen',
    // A wie Ampel. Kollidiert mit keinem der bisherigen Zeichen — auf dem
    // Tablet ist nur das Zeichen sichtbar.
    icon: 'A',
    title: 'Einschätzungen',
    subtitle: 'die Ampel, und wann zuletzt hingesehen wurde',
  },
  {
    path: '/fx-rates',
    label: 'Währungen',
    // X — frei, und in der Tabelle steht das Wort ohnehin daneben.
    icon: 'X',
    title: 'Währungen',
    subtitle: 'Referenzkurse der EZB, ein Wert je Bankarbeitstag',
  },
  {
    path: '/quotes',
    label: 'Kurse',
    icon: 'K',
    title: 'Kurse',
    subtitle: 'Beobachtete Kurse',
  },
  {
    path: '/price-history',
    label: 'Historische Kurse',
    icon: 'H',
    title: 'Historische Kurse',
    subtitle: 'Handelstage, End of Day',
  },
  {
    path: '/news',
    label: 'News',
    icon: 'N',
    title: 'News',
    subtitle: 'Recherche und Marktnachrichten',
  },
  {
    path: '/events',
    label: 'Ereignisse',
    icon: 'V',
    title: 'Ereignisse',
    subtitle: 'Termine und Ausgänge — Unternehmen, Makro, Rohstoffe',
  },
  {
    path: '/research',
    label: 'Cases',
    // C wie Case — dieselbe Marke wie im Feed.
    icon: 'C',
    title: 'Recherche',
    subtitle: 'laufende Vorgänge zu Titeln — ändert keine Bewertung',
  },
  {
    path: '/reports/buy-the-dip',
    label: 'Buy the Dip',
    // D wie Dip. B waere naheliegend und ist an die Handelsplaetze vergeben —
    // auf dem Tablet ist nur das Zeichen sichtbar, und zwei gleiche waeren
    // dort nicht auseinanderzuhalten.
    icon: 'D',
    title: 'Buy the Dip',
    subtitle: 'abverkaufte Qualität mit beginnender Bodenbildung',
  },
  {
    path: '/reports/daily-stretch',
    label: 'Daily Stretch',
    // R wie Report. D stand zur Wahl, kollidiert aber mit nichts Bestehendem
    // — R sagt auf dem Tablet, wo nur das Zeichen sichtbar ist, die Kategorie.
    icon: 'R',
    title: 'Daily Stretch',
    subtitle: 'normaler Rücksetzer bei stabiler Verfassung',
  },
  {
    path: '/rss',
    label: 'Schlagzeilen',
    // S wie Schlagzeile. Kollidiert mit keinem bisherigen Zeichen.
    icon: 'S',
    title: 'Schlagzeilen',
    subtitle: 'aus hinterlegten RSS-Quellen — fluechtig, nicht gespeichert',
  },
  {
    path: '/trade-ideas',
    label: 'Handelsideen',
    // I wie Idee. Kollidiert mit keinem der bisherigen Zeichen.
    icon: 'I',
    title: 'Handelsideen',
    subtitle: 'Vorschläge samt Einstieg, Obergrenze und Ungültigkeit',
  },
  {
    path: '/feed',
    label: 'Feed',
    icon: 'F',
    title: 'Feed',
    subtitle: 'der letzte Stand, und was dazukommt',
  },
  {
    path: '/admin/imports',
    label: 'Importe',
    // P wie Pipeline. I liegt bei den Handelsideen, T bei den Instrumenten.
    icon: 'P',
    title: 'Importe',
    subtitle: 'was geliefert wurde und ob es ankam',
  },
  {
    path: '/admin/oauth',
    label: 'Zugänge',
    // M wie MCP. Kollidiert mit keinem der bisherigen Zeichen.
    icon: 'M',
    title: 'Zugänge',
    subtitle: 'Verbindungen und Token des MCP-Zugangs — wer sich womit angemeldet hat',
  },
  {
    path: '/components',
    label: 'Komponenten',
    // U, nicht K: Auf dem Tablet ist nur das Zeichen sichtbar, und K stand
    // schon fuer die Kurse. Zwei gleiche Zeichen sind dort nicht zu
    // unterscheiden.
    icon: 'U',
    title: 'Komponenten',
    subtitle: 'Abnahme des Grundgerüsts',
  },
]

//: Die Navigation, geordnet nach HERKUNFT der Daten.
//:
//: Der Schnitt ist in diesem System real: Marktdaten kommen ueber die
//: Importstrecke, News und Faelle ueber den MCP-Zugang, die Watchlist von
//: Hand. Wer wissen will, warum sich etwas von selbst aktualisiert und etwas
//: anderes nicht, findet die Antwort in der Gruppenueberschrift.
//:
//: Die Uebersicht und der Feed stehen OHNE Ueberschrift ganz oben: Sie sind
//: keine Eintraege unter vielen, sondern der Einstieg. Die Uebersicht zuerst,
//: weil sie die Frage des Morgens beantwortet — was ist neu, was ist faellig;
//: der Feed darunter ist die Ansicht, die auf einem unbedienten Bildschirm
//: laeuft.
export const NAVIGATION: NavSection[] = [
  { key: 'einstieg', entries: [pick('/'), pick('/feed')] },
  // Die Einschätzungen stehen NEBEN der Watchlist, nicht bei der Recherche:
  // Sie sind das laufende Urteil zu dem, was man ausgewählt hat.
  // Das Depot steht bei der Auswahl: Was man haelt, ist die engste Auswahl
  // von allen — und gebucht wird es von Hand, wie die Watchlist.
  { key: 'auswahl', label: 'Meine Auswahl',
    entries: [pick('/watchlist'), pick('/depots'), pick('/assessments')] },
  // Auswertungen stehen direkt hinter der Auswahl, weil sie GENAU sie
  // bewerten: Der Daily Stretch rechnet ueber die aktiven Eintraege der
  // Watchlist. Weiter unten stuende er von seiner Datenquelle getrennt.
  { key: 'reports', label: 'Reports',
    entries: [pick('/reports/daily-stretch'),
              pick('/reports/buy-the-dip')] },
  // Handelsideen stehen bei der Recherche, weil sie dieselbe HERKUNFT haben:
  // Ein Agent traegt sie ein. Anders als der Daily Stretch, der gerechnet wird.
  { key: 'recherche', label: 'Recherche',
    entries: [pick('/news'), pick('/events'), pick('/research'),
              pick('/trade-ideas'), pick('/rss')] },
  { key: 'markt', label: 'Marktdaten',
    entries: [pick('/quotes'), pick('/price-history'),
              pick('/instruments'), pick('/commodities'),
              pick('/fx-rates'),
              pick('/exchanges'), pick('/industries'),
              pick('/vocabulary')] },
  { key: 'intern', label: 'Intern', entries: [pick('/components')] },
  // Als EINZIGE Gruppe keine Fachdaten, sondern der Betrieb — deshalb ganz
  // unten. Die Reihenfolge der Gruppen folgt der Herkunft der Daten, und der
  // Betrieb ist keine Herkunft, sondern die Sicht darauf.
  { key: 'admin', label: 'Admin',
    entries: [pick('/admin/imports'), pick('/admin/oauth')] },
]

function pick(path: string) {
  const view = VIEWS.find((candidate) => candidate.path === path)!
  return { to: view.path, label: view.label, icon: view.icon }
}

/**
 * Titel und Untertitel der Kopfzeile, gesetzt von der Ansicht.
 *
 * Für Listen genügt die Tabelle oben — dort steht der Titel fest. Eine
 * Detailansicht kennt ihren Titel aber erst, wenn die Daten da sind: "Intel
 * Corporation" steht in keiner Routentabelle. Und `/instruments/4` findet in
 * VIEWS ohnehin nichts, weil dort der Pfad exakt verglichen wird.
 */
interface PageTitle { title: string; subtitle?: string }

const TitleContext = createContext<(title: PageTitle | null) => void>(() => {})

/**
 * Setzt Titel und Untertitel der Kopfzeile, solange die Ansicht sichtbar ist.
 *
 * Beim Verlassen wird zurückgesetzt: Sonst stünde der Name des zuletzt
 * angesehenen Instruments über der Liste, zu der man gerade zurückgekehrt ist.
 */
export function usePageTitle(title: string, subtitle?: string) {
  const set = useContext(TitleContext)
  useEffect(() => {
    set({ title, subtitle })
    return () => set(null)
  }, [set, title, subtitle])
}

const ActionsContext = createContext<(actions: ReactNode) => void>(() => {})

/**
 * Stellt ein Bedienelement rechts in die Kopfzeile.
 *
 * FÜR DAS, WAS DIE DARSTELLUNG ÄNDERT, nicht die Menge. Ein Filter gehört in
 * die Filterleiste über der Tabelle — er entscheidet, welche Zeilen kommen.
 * Die Zielwährung entscheidet nur, wie sie aussehen; sie steht deshalb über
 * der Ansicht und nicht in ihr.
 *
 * Beim Verlassen wird zurückgesetzt, wie beim Titel: Sonst stünde der
 * Währungsschalter über einer Liste, in der es nichts umzurechnen gibt.
 */
export function usePageActions(actions: ReactNode) {
  const set = useContext(ActionsContext)
  useEffect(() => {
    set(actions)
    return () => set(null)
  }, [set, actions])
}

/**
 * Der Rahmen um alle angemeldeten Ansichten.
 *
 * Die Ansichten liefern nur ihren Inhalt. Trüge jede ihren eigenen `AppShell`,
 * stünde die Navigation in jeder Datei erneut — und der nächste Eintrag müsste
 * an so vielen Stellen ergänzt werden, wie es Seiten gibt.
 */
export function AppLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const session = useSession()
  const [fromPage, setFromPage] = useState<PageTitle | null>(null)
  const [actions, setActions] = useState<ReactNode>(null)

  const view = VIEWS.find((candidate) => candidate.path === location.pathname)

  // Was die Ansicht meldet, hat Vorrang: Sie weiß mehr als die Routentabelle.
  const title = fromPage?.title ?? view?.title ?? 'Instrument Watcher'
  const subtitle = fromPage?.subtitle ?? view?.subtitle

  return (
    <TitleContext.Provider value={setFromPage}>
      <AppShell
        navigation={NAVIGATION}
        user={session?.user ?? '—'}
        // Der Ereignisstrom traegt die Zugangsdaten — er darf sie nicht
        // ueberleben.
        onLogout={() => {
          closeEventStream()
          clearAuth()
          navigate('/login')
        }}
        title={title}
        subtitle={subtitle}
        actions={actions}
      >
        <ActionsContext.Provider value={setActions}>
          <Outlet />
        </ActionsContext.Provider>
      </AppShell>
    </TitleContext.Provider>
  )
}
